import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  HardDrive, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR",
      { day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 60) return `il y a ${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.round(h / 24)} j`;
  } catch { return iso; }
}

const STATUS_STYLE = {
  open: "border-red-200 bg-red-50 text-red-800",
  resolved: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

export default function BackupAlerts() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canScan = hasPermission("tenant.update");

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("open");

  const load = async () => {
    try {
      const r = await api.get(`/purchases/files/backup-alerts?status=${filter === "all" ? "" : filter}`);
      setAlerts(r.data.alerts || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const scanNow = async () => {
    setScanning(true);
    try {
      const r = await api.post("/purchases/files/backup-alerts/scan-now");
      const s = r.data.summary || {};
      toast.success(t("backupAlerts.scanDone", {
        opened: s.opened || 0,
        resolved: s.resolved || 0,
      }));
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const openCount = alerts.filter(a => a.status === "open").length;

  return (
    <div className="space-y-6" data-testid="backup-alerts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            {t("sidebar.backupAlerts")}
          </div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("backupAlerts.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("backupAlerts.subtitle")}</p>
        </div>
        {canScan && (
          <Button variant="outline" onClick={scanNow} disabled={scanning}
                  data-testid="backup-alerts-scan-btn">
            {scanning
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={2} />}
            {t("backupAlerts.scanNow")}
          </Button>
        )}
      </div>

      {/* Status banner */}
      <Card className={cn(
        "p-5 flex items-start gap-3",
        openCount === 0
          ? "border-emerald-200 bg-emerald-50/60"
          : "border-red-200 bg-red-50/60")}
            data-testid="backup-alerts-banner">
        {openCount === 0 ? (
          <>
            <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              <div className="font-medium text-emerald-900">
                {t("backupAlerts.healthyTitle")}
              </div>
              <div className="text-xs text-emerald-800 mt-1">
                {t("backupAlerts.healthyDesc")}
              </div>
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              <div className="font-medium text-red-900">
                {t("backupAlerts.degradedTitle", { count: openCount })}
              </div>
              <div className="text-xs text-red-800 mt-1">
                {t("backupAlerts.degradedDesc")}
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Filter tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="open" data-testid="backup-filter-open">
            {t("backupAlerts.filter.open")}
            {openCount > 0 && filter !== "open" && (
              <Badge variant="outline" className="ml-2 border-red-200 bg-red-50 text-red-700 text-[10px]">
                {openCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved" data-testid="backup-filter-resolved">
            {t("backupAlerts.filter.resolved")}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="backup-filter-all">
            {t("backupAlerts.filter.all")}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Alerts table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
          </div>
        ) : alerts.length === 0 ? (
          <div className="py-12 text-center" data-testid="backup-alerts-empty">
            <HardDrive className="h-8 w-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-sm text-slate-500">{t("backupAlerts.emptyState")}</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("backupAlerts.col.backend")}</TableHead>
                <TableHead>{t("backupAlerts.col.status")}</TableHead>
                <TableHead className="text-right">{t("backupAlerts.col.failedCount")}</TableHead>
                <TableHead>{t("backupAlerts.col.openedAt")}</TableHead>
                <TableHead>{t("backupAlerts.col.lastSeen")}</TableHead>
                <TableHead>{t("backupAlerts.col.resolvedAt")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map(a => (
                <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">
                    {a.backend === "s3" ? "S3 (Scaleway)" : "MongoDB / GridFS"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline"
                           className={cn("text-[10px]", STATUS_STYLE[a.status] || "")}>
                      {a.status === "open" ? (
                        <><AlertTriangle className="h-3 w-3 mr-1" strokeWidth={2} />
                        {t("backupAlerts.statusOpen")}</>
                      ) : (
                        <><CheckCircle2 className="h-3 w-3 mr-1" strokeWidth={2} />
                        {t("backupAlerts.statusResolved")}</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {a.current_count ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {fmtDateTime(a.opened_at)}
                    <div className="text-[10px] text-slate-400">{fmtRelative(a.opened_at)}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {fmtRelative(a.last_seen_at)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {a.resolved_at ? fmtDateTime(a.resolved_at) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
